import React from "react";

/**
 * Template #4 — highlight_overlay (3D spotlight)
 * Focus attention without changing layout.
 * Parts: overlay
 * Actions: draw, fade_in, fade_out, pulse
 *
 * 3D volumetric spotlight cone with depth lighting.
 */

export interface HighlightOverlayDomainProps {
  cx?: number;
  cy?: number;
  radius?: number;
  overlayOpacity?: number;
  pulse?: boolean;
  accentColor?: string;
  opacity?: number;
}

export const HighlightOverlayDomain: React.FC<HighlightOverlayDomainProps> = ({
  cx = 400,
  cy = 225,
  radius = 80,
  overlayOpacity = 0.5,
  pulse = false,
  accentColor = "#facc15",
  opacity = 1,
}) => {
  const uid = `spot-${Math.round(cx)}-${Math.round(cy)}`;

  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id={`${uid}-grad`} cx="50%" cy="45%">
          <stop offset="0%" stopColor={accentColor} stopOpacity="0.08" />
          <stop offset="60%" stopColor="transparent" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <mask id={`${uid}-mask`}>
          <rect x="0" y="0" width="800" height="450" fill="white" />
          <circle cx={cx} cy={cy} r={radius} fill="black" />
        </mask>
      </defs>

      {/* Dimmed surround */}
      <rect x="0" y="0" width="800" height="450" fill="#0f172a" opacity={overlayOpacity} mask={`url(#${uid}-mask)`} />

      {/* Volumetric light cone from above */}
      <polygon
        points={`${cx - 20},0 ${cx + 20},0 ${cx + radius * 0.8},${cy - radius * 0.3} ${cx - radius * 0.8},${cy - radius * 0.3}`}
        fill={accentColor}
        opacity={0.04}
      />

      {/* Inner glow */}
      <circle cx={cx} cy={cy} r={radius} fill={`url(#${uid}-grad)`} />

      {/* Spotlight ring — 3D with double stroke */}
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke={accentColor} strokeWidth={2.5} opacity={0.5} />
      <circle cx={cx} cy={cy} r={radius - 3} fill="none" stroke="#fff" strokeWidth={0.5} opacity={0.08} />

      {/* Floor reflection ellipse */}
      <ellipse cx={cx} cy={cy + radius * 0.7} rx={radius * 0.6} ry={6} fill={accentColor} opacity={0.06} />

      {/* Pulse ring */}
      {pulse && (
        <circle cx={cx} cy={cy} r={radius + 8} fill="none" stroke={accentColor} strokeWidth={1.5} opacity={0.25}>
          <animate attributeName="r" values={`${radius + 4};${radius + 18};${radius + 4}`} dur="2.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.25;0.05;0.25" dur="2.5s" repeatCount="indefinite" />
        </circle>
      )}
    </g>
  );
};
